import { format } from 'date-fns';
import {
  Correlation,
  ViewerGraphData,
  ViewerNode,
} from '../../components/link-analysis/viewer/models/graph-viewer.models';
import {
  deriveMeta,
  getDegreeMap,
} from '../../components/link-analysis/viewer/utils/graph-viewer-data.util';
import {
  collectMergedPlatformDisplayFields,
  displayLabelForFieldKey,
} from '../../utils/canonical-field-key';

export interface LinkAnalysisDossierPdfInput {
  investigation: any;
  graphData: ViewerGraphData;
  correlations: Correlation[];
  graphScreenshotDataUrl?: string | null;
  evidenceImages?: DossierEmbeddedImage[];
  exportedBy?: string;
}

export interface DossierImageSource {
  label: string;
  url: string;
}

export interface DossierEmbeddedImage {
  label: string;
  dataUrl: string;
}

export interface DossierSubject {
  /** Person name when discovered, otherwise the investigation root value. */
  name: string;
  rootType: string;
  rootTypeLabel: string;
  rootValue: string;
  /** Additional discovered identifiers (email, phone, username). */
  discovered: Array<{ label: string; value: string }>;
}

const ROOT_TYPE_LABELS: Record<string, string> = {
  phone: 'Phone',
  email: 'Email',
  username: 'Username',
};

const INTERNAL_DETAIL_KEYS = new Set([
  'layer',
  'parentNodeId',
  'parentActionId',
  'actionType',
  'sourceContext',
  'directQueryLinked',
  'drillDownQueryType',
  'drillDownExpandable',
  'hiddenEvidenceCount',
  'officialSignalPresent',
  'isStarter',
  'isAggregateCollection',
  'collectionType',
  'contradictionFlags',
  'liveWebUsed',
  'generatedAt',
  'role',
  'evidenceTier',
  'status',
  'trace',
  'sections',
  'sourceData',
  'normalizedResult',
  'searchableValues',
  'hintValues',
  'hasAccount',
  'sourceCount',
  'queryType',
  'searchableCount',
  'platform',
]);

const SKIP_DETAIL_KEYS = new Set([
  'uid',
  'has_account',
  'searchablecount',
]);

const FIELD_LABEL_MAP: Record<string, string> = {
  'im id': 'Profile ID',
  email: 'Email',
  carrier: 'Carrier',
  location: 'Location',
  verified: 'Verified',
  'number type': 'Number Type',
  picture_url: 'Profile Photo',
  'is truecaller user': 'Truecaller User',
  'phone country code': 'Country',
  name: 'Name',
  username: 'Username',
  url: 'Profile URL',
};

const OSINT_DETAIL_ORDER = [
  'Platform',
  'Name',
  'Email',
  'Username',
  'Carrier',
  'Location',
  'Number Type',
  'Country',
  'Verified',
  'Truecaller User',
  'Profile ID',
  'Profile Photo',
  'Profile URL',
];

function isLikelyImageUrl(value: unknown): value is string {
  const url = String(value || '').trim();
  if (!/^https?:\/\//i.test(url)) return false;
  if (/marker-icon\.png$/i.test(url)) return false;
  if (/leaflet@.*\/images\/marker/i.test(url)) return false;
  return true;
}

export function collectDossierImageSources(
  investigation: any,
  graphData: ViewerGraphData,
): DossierImageSource[] {
  const images = new Map<string, string>();

  const addImage = (url: unknown, label: string): void => {
    if (!isLikelyImageUrl(url)) return;
    const normalizedUrl = url.trim();
    if (!images.has(normalizedUrl)) {
      images.set(normalizedUrl, label.trim() || 'Evidence image');
    }
  };

  const photoRefs = investigation?.photo_refs;
  if (Array.isArray(photoRefs)) {
    photoRefs.forEach((ref: any, index: number) => {
      const label = ref?.label || `Uploaded photo ${index + 1}`;
      addImage(ref?.url, label);
      addImage(ref?.image_url, label);
      addImage(ref?.thumbnail, label);
    });
  }

  graphData.nodes.forEach((node) => {
    const raw = node.raw || {};
    const metadata = raw.metadata || {};
    const platform = metadata.platform || node.label || 'Source';
    const baseLabel =
      raw.type === 'image' || raw.type === 'face_result'
        ? String(node.label || 'Image')
        : `${platform} profile photo`;

    addImage(raw.imageUrl, baseLabel);
    addImage(metadata.imageUrl, baseLabel);

    const pictureField = metadata.sourceData?.fields?.picture_url;
    if (pictureField) {
      addImage(readRawFieldValue(pictureField), `${platform} profile photo`);
    }

    if (metadata.collectionType === 'image' || raw.type === 'image') {
      addImage(raw.imageUrl, String(node.label || 'Collected image'));
      const collected = metadata.collectedValues;
      if (Array.isArray(collected)) {
        collected.forEach((value: unknown, index: number) => {
          addImage(value, `Collected image ${index + 1}`);
        });
      }
    }

    if (raw.type === 'face_result') {
      addImage(raw.imageUrl, String(node.label || 'Face match'));
      addImage(metadata.queryValue, String(node.label || 'Face match'));
    }
  });

  return Array.from(images.entries()).map(([url, label]) => ({ url, label }));
}

function buildEvidenceImagesSection(
  images: DossierEmbeddedImage[],
): Record<string, unknown>[] {
  if (!images.length) return [];

  const blocks: Record<string, unknown>[] = [
    {
      text: 'Evidence Images',
      fontSize: 13,
      bold: true,
      color: '#0f172a',
      margin: [0, 14, 0, 8],
      pageBreak: 'before',
    },
  ];

  images.forEach((image, index) => {
    blocks.push({
      text: image.label,
      fontSize: 9,
      bold: true,
      color: '#334155',
      margin: [0, index === 0 ? 0 : 10, 0, 4],
    });
    blocks.push({
      image: image.dataUrl,
      width: 220,
      margin: [0, 0, 0, 6],
    });
  });

  return blocks;
}

function titleCase(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function isCollectionNode(node: ViewerNode): boolean {
  const rawType = String(node.raw?.type || '');
  return (
    rawType === 'collection' ||
    rawType === 'aggregate_phones' ||
    rawType === 'aggregate_emails' ||
    rawType.startsWith('collected') ||
    rawType.startsWith('aggregate_') ||
    node.id.startsWith('collection:') ||
    node.id.startsWith('aggregate:')
  );
}

function isWorkspaceNode(node: ViewerNode): boolean {
  return node.raw?.type === 'starter' || node.id === 'starter';
}

function isAiAnalysisNode(node: ViewerNode): boolean {
  return node.raw?.type === 'ai_analysis';
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
}

function readFieldValue(field: any): string | null {
  if (field == null) return null;
  if (typeof field === 'string' || typeof field === 'number' || typeof field === 'boolean') {
    return String(field);
  }
  if (typeof field === 'object' && field.value != null && field.value !== '') {
    return String(field.value);
  }
  return null;
}

function readRawFieldValue(field: any): unknown {
  if (field == null) return null;
  if (typeof field === 'string' || typeof field === 'number' || typeof field === 'boolean') {
    return field;
  }
  if (typeof field === 'object' && 'value' in field) {
    return field.value;
  }
  return null;
}

function collectNamesFromNode(raw: any): string[] {
  if (!raw || raw.type === 'ai_analysis' || raw.type === 'starter') return [];
  const metadata = raw.metadata || {};
  const names: string[] = [];
  const rawType = raw.type;

  if (rawType === 'phone' || rawType === 'email' || rawType === 'person') {
    const normalizedNames = metadata.normalizedResult?.name;
    if (Array.isArray(normalizedNames)) {
      names.push(...normalizedNames.map(String));
    }
    if (Array.isArray(metadata.extractedNames)) {
      names.push(...metadata.extractedNames.map(String));
    }
    if (raw.label && rawType === 'person') {
      names.push(String(raw.label));
    }
  }

  if (rawType === 'osint_source') {
    const accounts = metadata.sourceData?.accounts;
    if (Array.isArray(accounts)) {
      accounts.forEach((account: any) => {
        const name = readFieldValue(account?.name);
        if (name) names.push(name);
      });
    }
  }

  return names;
}

function scorePersonName(name: string): number {
  const trimmed = name.trim();
  if (!trimmed || trimmed.length < 2) return -1;
  if (trimmed.includes('...')) return 1;
  if (/^\+?\d[\d\s()-]{6,}$/.test(trimmed)) return -1;
  if (trimmed.includes('@')) return -1;
  if (/^(social media|technology|education|freelance|development|jobs|forums|entertainment|artificial intelligence)$/i.test(trimmed)) {
    return -1;
  }
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2 && words.every((word) => /^[A-Za-z][A-Za-z.'-]*$/.test(word))) {
    return 100;
  }
  if (words.length === 1 && /^[A-Za-z][A-Za-z0-9._-]*$/.test(words[0])) {
    return 10;
  }
  return 0;
}

function pickBestPersonName(names: string[], rootValue: string): string | null {
  const ranked = uniqueStrings(names)
    .filter((name) => name !== rootValue && name !== 'Start investigation')
    .map((name) => ({ name, score: scorePersonName(name) }))
    .filter((entry) => entry.score >= 0)
    .sort((a, b) => b.score - a.score);
  return ranked[0]?.name || null;
}

function collectEmailsFromGraph(graphData: ViewerGraphData): string[] {
  const emails: string[] = [];
  graphData.nodes.forEach((node) => {
    const raw = node.raw;
    const metadata = raw?.metadata || {};
    if (raw?.type === 'email' && !isCollectionNode(node) && node.label) {
      emails.push(node.label);
    }
    const normalizedEmail = metadata.normalizedResult?.email;
    if (Array.isArray(normalizedEmail)) emails.push(...normalizedEmail.map(String));
    const searchableEmail =
      metadata.searchable?.email || metadata.searchableValues?.email;
    if (Array.isArray(searchableEmail)) {
      emails.push(
        ...searchableEmail.map(String).filter((email) => email && !/\*{2,}/.test(email)),
      );
    }
    const aggregateItems = Array.isArray(metadata.items) ? metadata.items : [];
    if (raw?.type === 'aggregate_emails' || metadata.collectionType === 'email') {
      aggregateItems.forEach((item: any) => {
        if (item?.value) emails.push(String(item.value));
      });
    }
    Object.values(metadata.sourceData?.fields || {}).forEach((field: any) => {
      const fieldKey = String(field?.key || '');
      if (fieldKey.includes('email_hint')) return;
      if (field?.['osint-search-type'] === 'email_hint') return;
      if (field?.['osint-search-type'] === 'email' || field?.type === 'email') {
        const value = readFieldValue(field);
        if (value && !/\*{2,}/.test(value)) emails.push(value);
      }
    });
  });
  return uniqueStrings(emails);
}

function collectPhonesFromGraph(graphData: ViewerGraphData): string[] {
  const phones: string[] = [];
  graphData.nodes.forEach((node) => {
    const raw = node.raw;
    const metadata = raw?.metadata || {};
    if (raw?.type === 'phone' && !isCollectionNode(node) && !isWorkspaceNode(node) && node.label) {
      phones.push(node.label);
    }
    const normalizedNumber = metadata.normalizedResult?.number;
    if (normalizedNumber) phones.push(String(normalizedNumber));
    const searchablePhone =
      metadata.searchable?.phone || metadata.searchableValues?.phone;
    if (Array.isArray(searchablePhone)) phones.push(...searchablePhone.map(String));
    const aggregateItems = Array.isArray(metadata.items) ? metadata.items : [];
    if (raw?.type === 'aggregate_phones' || metadata.collectionType === 'phone') {
      aggregateItems.forEach((item: any) => {
        if (item?.value) phones.push(String(item.value));
      });
    }
  });
  return uniqueStrings(phones).filter(
    (phone) => phone !== 'Start investigation' && !phone.toLowerCase().includes('collected phones'),
  );
}

function collectUsernamesFromGraph(graphData: ViewerGraphData): string[] {
  const usernames: string[] = [];
  graphData.nodes.forEach((node) => {
    const metadata = node.raw?.metadata || {};
    if (metadata.handle) usernames.push(String(metadata.handle));
    const searchable = metadata.searchableValues?.username;
    if (Array.isArray(searchable)) usernames.push(...searchable.map(String));
    if (node.details?.['username']) usernames.push(String(node.details['username']));
  });
  return uniqueStrings(usernames);
}

export function resolveDossierSubject(
  investigation: any,
  graphData: ViewerGraphData,
): DossierSubject {
  const summary = investigation?.summary || investigation?.graph_payload?.summary || {};
  const rootType = String(
    investigation?.root_type || summary.rootType || 'phone',
  ).toLowerCase();
  const rootValue = String(
    investigation?.root_value || summary.rootValue || '—',
  ).trim();
  const rootTypeLabel = ROOT_TYPE_LABELS[rootType] || titleCase(rootType);

  const discoveredNames = graphData.nodes.flatMap((node) => collectNamesFromNode(node.raw));
  const bestName = pickBestPersonName(discoveredNames, rootValue);
  const subjectName = bestName || rootValue || 'Subject';

  const discovered: Array<{ label: string; value: string }> = [
    { label: rootTypeLabel, value: rootValue || '—' },
  ];

  const emails = collectEmailsFromGraph(graphData).filter((value) => value !== rootValue);
  const phones = collectPhonesFromGraph(graphData).filter((value) => value !== rootValue);
  const usernames = collectUsernamesFromGraph(graphData).filter((value) => value !== rootValue);

  if (rootType !== 'email' && emails.length) {
    discovered.push({ label: 'Email', value: emails[0] });
  }
  if (rootType !== 'phone' && phones.length) {
    discovered.push({ label: 'Phone', value: phones[0] });
  }
  if (rootType !== 'username' && usernames.length) {
    discovered.push({ label: 'Username', value: usernames[0] });
  }

  return {
    name: subjectName,
    rootType,
    rootTypeLabel,
    rootValue: rootValue || '—',
    discovered,
  };
}

function formatDetailValue(label: string, value: unknown): string {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  const text = String(value).trim();
  if (label === 'Location') {
    return normalizeLocationText(text);
  }
  return text;
}

function normalizeLocationText(value: string): string {
  const city = value.match(/City:\s*([^]+?)(?:\s+Country:|$)/i)?.[1]?.trim();
  const country = value.match(/Country:\s*(\S+)/i)?.[1]?.trim();
  const timezone = value.match(/Timezone:\s*(\S+)/i)?.[1]?.trim();
  if (!city && !country && !timezone) return value;

  const parts = [city, country].filter(Boolean);
  let result = parts.join(', ');
  if (timezone) {
    result = result ? `${result} (${timezone})` : timezone;
  }
  return result;
}

function appendDetail(
  details: Map<string, string>,
  label: string,
  value: unknown,
): void {
  if (value == null || value === '') return;
  const normalizedLabel = label.trim();
  const normalizedValue = formatDetailValue(normalizedLabel, value).trim();
  if (!normalizedLabel || !normalizedValue) return;
  if (!details.has(normalizedLabel)) {
    details.set(normalizedLabel, normalizedValue);
  }
}

function normalizeFieldLabel(key: string): string {
  const mapped = FIELD_LABEL_MAP[key.trim().toLowerCase()];
  if (mapped) return mapped;
  return titleCase(key.replace(/_/g, ' '));
}

function orderDetailItems(items: Map<string, string>, preferredOrder: string[]): string[] {
  const ordered: string[] = [];
  preferredOrder.forEach((label) => {
    const value = items.get(label);
    if (value) ordered.push(`${label}: ${value}`);
  });
  items.forEach((value, label) => {
    if (!preferredOrder.includes(label)) {
      ordered.push(`${label}: ${value}`);
    }
  });
  return ordered;
}

function collectNodeDetailItems(node: ViewerNode): string[] {
  const raw = node.raw || {};
  const metadata = raw.metadata || {};
  const details = new Map<string, string>();

  if (isWorkspaceNode(node)) {
    return ['Investigation workspace root'];
  }

  if (isAiAnalysisNode(node)) {
    appendDetail(details, 'Mode', metadata.mode);
    appendDetail(details, 'Query', metadata.queryValue);
    if (metadata.summary) appendDetail(details, 'Summary', metadata.summary);
    if (metadata.title) appendDetail(details, 'Title', metadata.title);
    if (Array.isArray(metadata.sections)) {
      metadata.sections.forEach((section: any, index: number) => {
        if (section?.sectionTitle) {
          appendDetail(details, `Finding ${index + 1}`, section.sectionTitle);
        }
        if (section?.summary) {
          appendDetail(details, `Summary ${index + 1}`, section.summary);
        }
      });
    }
    return orderDetailItems(details, ['Mode', 'Query', 'Title', 'Summary']);
  }

  if (raw.type === 'osint_source') {
    appendDetail(details, 'Platform', metadata.platform || node.label);

    const seenCanonical = new Set<string>(['platform']);
    collectMergedPlatformDisplayFields(metadata.sourceData || {}).forEach((item) => {
      if (SKIP_DETAIL_KEYS.has(item.key) || SKIP_DETAIL_KEYS.has(`${item.key}s`)) return;
      if (seenCanonical.has(item.key)) return;
      seenCanonical.add(item.key);
      const label =
        item.label ||
        FIELD_LABEL_MAP[item.key] ||
        displayLabelForFieldKey(item.key);
      const rawValue =
        item.values.length <= 1 ? item.value : item.values;
      if (rawValue == null || rawValue === '') return;
      appendDetail(details, label, rawValue);
    });
  } else if (raw.type === 'phone' || raw.type === 'email' || raw.type === 'username') {
    appendDetail(details, titleCase(raw.type), node.label);
    const normalized = metadata.normalizedResult || {};
    if (Array.isArray(normalized.name)) appendDetail(details, 'Name', normalized.name.join(', '));
    if (Array.isArray(normalized.email)) appendDetail(details, 'Email', normalized.email.join(', '));
    if (normalized.number) appendDetail(details, 'Number', normalized.number);
    if (Array.isArray(metadata.emailHints) && metadata.emailHints.length) {
      appendDetail(details, 'Email Hints', metadata.emailHints.join(', '));
    }
  } else if (isCollectionNode(node)) {
    const searchable = metadata.searchableValues || {};
    Object.entries(searchable).forEach(([key, values]) => {
      if (Array.isArray(values) && values.length) {
        appendDetail(details, titleCase(key), values.join(', '));
      }
    });
    if (metadata.collectionType) {
      appendDetail(details, 'Collection', titleCase(String(metadata.collectionType)));
    }
  } else {
    Object.entries(node.details || {}).forEach(([key, value]) => {
      if (!INTERNAL_DETAIL_KEYS.has(key)) {
        appendDetail(details, normalizeFieldLabel(key), value);
      }
    });
  }

  if (raw.imageUrl && !details.has('Profile Photo')) {
    appendDetail(details, 'Profile Photo', raw.imageUrl);
  }

  if (raw.type === 'osint_source') {
    return orderDetailItems(details, OSINT_DETAIL_ORDER);
  }

  return Array.from(details.entries()).map(([label, value]) => `${label}: ${value}`);
}

function buildNodeDetailsCell(node: ViewerNode): Record<string, unknown> {
  const items = collectNodeDetailItems(node);
  if (!items.length) {
    return cell('—', { fontSize: 7, color: '#64748b' });
  }
  if (items.length === 1) {
    return cell(items[0], { fontSize: 7, color: '#64748b' });
  }
  return {
    ul: items,
    fontSize: 7,
    color: '#64748b',
    margin: [0, 1, 0, 1],
  };
}

const PDF_GROUP_ORDER = [
  'person',
  'phone',
  'email',
  'social',
  'ai_analysis',
  'collection',
  'starter',
  'location',
  'image',
] as const;

const PDF_GROUP_LABELS: Record<string, string> = {
  person: 'Person',
  phone: 'Phone',
  email: 'Email',
  social: 'Social Profile',
  ai_analysis: 'AI Analysis',
  collection: 'Collections',
  starter: 'Workspace',
  location: 'Location',
  image: 'Image',
};

function getPdfGroupKey(node: ViewerNode): string {
  const rawType = String(node.raw?.type || '');
  if (rawType === 'ai_analysis') return 'ai_analysis';
  if (rawType === 'starter') return 'starter';
  if (rawType === 'collection' || rawType.startsWith('collected') || node.id.startsWith('collection:')) {
    return 'collection';
  }
  if (rawType === 'osint_source') return 'social';
  return node.type;
}

function cell(text: unknown, style: Record<string, unknown> = {}): Record<string, unknown> {
  return { text: stringify(text), fontSize: 8, color: '#334155', ...style };
}

function headerCell(text: string): Record<string, unknown> {
  return { text, bold: true, fontSize: 8, fillColor: '#f1f5f9', color: '#475569' };
}

function stringify(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => stringify(item))
      .filter((part) => part && part !== '—');
    return parts.length ? parts.join('\n') : '—';
  }
  if (typeof value === 'object') {
    try {
      const parts = Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== null && v !== undefined && !(typeof v === 'string' && !String(v).trim()))
        .map(([k, v]) => `${k}: ${stringify(v)}`);
      return parts.length ? parts.join('\n') : '—';
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function riskBadge(risk: string): Record<string, unknown> {
  const color =
    risk === 'high' ? '#b91c1c' : risk === 'medium' ? '#b45309' : '#475569';
  return cell(risk.toUpperCase(), { bold: true, color, fontSize: 7 });
}

function strengthBadge(strength: string): Record<string, unknown> {
  const strong = strength === 'strong';
  return cell(strength.toUpperCase(), {
    bold: true,
    color: strong ? '#b91c1c' : '#b45309',
    alignment: 'right',
    fontSize: 7,
  });
}

function buildSummaryCards(
  nodeCount: number,
  edgeCount: number,
  correlationCount: number,
  highRisk: number,
): Record<string, unknown> {
  const cards = [
    { label: 'Entities', value: nodeCount, color: '#dbeafe', text: '#1d4ed8' },
    { label: 'Relationships', value: edgeCount, color: '#d1fae5', text: '#047857' },
    { label: 'Correlations', value: correlationCount, color: '#ede9fe', text: '#6d28d9' },
    { label: 'High Risk', value: highRisk, color: '#fee2e2', text: '#b91c1c' },
  ];

  return {
    columns: cards.map((card) => ({
      width: '*',
      margin: [0, 0, 6, 0],
      table: {
        widths: ['*'],
        body: [
          [
            {
              stack: [
                { text: String(card.value), fontSize: 18, bold: true, color: card.text, margin: [0, 0, 0, 2] },
                { text: card.label, fontSize: 8, color: card.text },
              ],
              fillColor: card.color,
              margin: [8, 8, 8, 8],
            },
          ],
        ],
      },
      layout: 'noBorders',
    })),
    margin: [0, 0, 0, 10],
  };
}

function buildCorrelationsSection(
  correlations: Correlation[],
  nodeMap: Map<string, ViewerNode>,
): Record<string, unknown>[] {
  if (!correlations.length) return [];

  const blocks: Record<string, unknown>[] = [
    { text: 'Key Correlations', fontSize: 13, bold: true, color: '#0f172a', margin: [0, 14, 0, 8] },
  ];

  correlations.forEach((item) => {
    blocks.push({
      table: {
        widths: ['*', 58],
        body: [
          [
            {
              stack: [
                { text: item.attribute, fontSize: 9, bold: true, color: '#0f172a' },
                {
                  text: item.nodeIds.map((id) => nodeMap.get(id)?.label ?? id).join(', '),
                  fontSize: 7,
                  color: '#64748b',
                  margin: [0, 2, 0, 0],
                },
              ],
              margin: [8, 6, 8, 6],
            },
            { ...strengthBadge(item.strength), margin: [0, 8, 8, 0] as [number, number, number, number] },
          ],
        ],
      },
      layout: {
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => '#e2e8f0',
        vLineColor: () => '#e2e8f0',
      },
      margin: [0, 0, 0, 6],
    });
  });

  return blocks;
}

function buildEntityInventorySection(
  graphData: ViewerGraphData,
  degreeMap: Record<string, number>,
): Record<string, unknown>[] {
  const grouped = PDF_GROUP_ORDER.map((groupKey) => ({
    groupKey,
    nodes: graphData.nodes.filter((node) => getPdfGroupKey(node) === groupKey),
  })).filter((group) => group.nodes.length > 0);

  const blocks: Record<string, unknown>[] = [
    { text: 'Entity Inventory', fontSize: 13, bold: true, color: '#0f172a', margin: [0, 14, 0, 8] },
  ];

  grouped.forEach((group) => {
    blocks.push({
      text: `${PDF_GROUP_LABELS[group.groupKey] || titleCase(group.groupKey)} (${group.nodes.length})`,
      fontSize: 9,
      bold: true,
      color: '#334155',
      margin: [0, 6, 0, 4],
    });
    blocks.push({
      table: {
        headerRows: 1,
        widths: [110, 34, 34, 40, '*'],
        body: [
          [
            headerCell('Entity'),
            headerCell('Links'),
            headerCell('Conf.'),
            headerCell('Risk'),
            headerCell('Details'),
          ],
          ...group.nodes.map((node) => {
            const meta = deriveMeta(node, degreeMap[node.id] ?? 0);
            return [
              cell(node.label, { bold: true, color: '#0f172a' }),
              cell(degreeMap[node.id] ?? 0),
              cell(`${meta.confidence}%`),
              riskBadge(meta.risk),
              buildNodeDetailsCell(node),
            ];
          }),
        ],
      },
      layout: {
        hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length ? 1 : 0.5),
        vLineWidth: () => 0,
        hLineColor: () => '#e2e8f0',
        paddingLeft: () => 4,
        paddingRight: () => 4,
        paddingTop: () => 4,
        paddingBottom: () => 4,
      },
      margin: [0, 0, 0, 10],
    });
  });

  return blocks;
}

function getPdfEntityTypeLabel(node: ViewerNode): string {
  const groupKey = getPdfGroupKey(node);
  return PDF_GROUP_LABELS[groupKey] || titleCase(groupKey);
}

const ACTION_TYPE_LABELS: Record<string, string> = {
  osint_search: 'OSINT search',
  ai_analysis: 'AI analysis',
  leak_search: 'Leak search',
  face_search: 'Face search',
};

function formatParentLabel(
  parentId: string,
  nodeMap: Map<string, ViewerNode>,
  rootValue?: string,
): string {
  if (!parentId) return '—';
  if (parentId === 'starter') return rootValue || 'Start investigation';
  const parentNode = nodeMap.get(parentId);
  if (parentNode) return parentNode.label;
  const separator = parentId.indexOf(':');
  if (separator > -1) return parentId.slice(separator + 1);
  return parentId;
}

function formatActionLabel(actionType: unknown, isWorkspace: boolean): string {
  if (isWorkspace) return 'Investigation start';
  if (!actionType) return '—';
  const key = String(actionType).toLowerCase();
  return ACTION_TYPE_LABELS[key] || titleCase(key);
}

function getLayerDescription(layer: number, investigation: any, subject: DossierSubject): string {
  if (layer === 0) {
    return `Root — ${subject.rootTypeLabel} ${subject.rootValue}`;
  }
  if (layer === 1) {
    const rootType = String(investigation?.root_type || 'query').toLowerCase();
    return `First layer — direct results from the root ${rootType} search`;
  }
  return `Layer ${layer} — entities discovered through drill-down from prior layers`;
}

interface InvestigationLayerRow {
  layer: number;
  entity: string;
  entityType: string;
  parent: string;
  action: string;
  relationship: string;
}

function collectInvestigationLayerRows(
  graphData: ViewerGraphData,
  nodeMap: Map<string, ViewerNode>,
  subject: DossierSubject,
): InvestigationLayerRow[] {
  const edgeLabels = new Map<string, string>();
  graphData.edges.forEach((edge) => {
    edgeLabels.set(`${edge.source}::${edge.target}`, edge.label);
    edgeLabels.set(`${edge.target}::${edge.source}`, edge.label);
  });

  const rows: InvestigationLayerRow[] = [];

  graphData.nodes.forEach((node) => {
    const metadata = node.raw?.metadata || {};
    const layerValue = metadata.layer;
    if (layerValue == null || layerValue === '') return;

    const layer = Number(layerValue);
    if (Number.isNaN(layer)) return;

    const workspace = isWorkspaceNode(node);
    const parentId = String(metadata.parentNodeId || '');
    const parent = workspace ? '—' : formatParentLabel(parentId, nodeMap, subject.rootValue);
    const action = formatActionLabel(metadata.actionType, workspace);
    const relationship =
      workspace
        ? '—'
        : edgeLabels.get(`${parentId}::${node.id}`)
          || edgeLabels.get(`${node.id}::${parentId}`)
          || 'linked';

    rows.push({
      layer,
      entity: workspace ? subject.rootValue : node.label,
      entityType: workspace ? subject.rootTypeLabel : getPdfEntityTypeLabel(node),
      parent,
      action,
      relationship,
    });
  });

  return rows.sort((a, b) => {
    if (a.layer !== b.layer) return a.layer - b.layer;
    if (a.parent !== b.parent) return a.parent.localeCompare(b.parent);
    return a.entity.localeCompare(b.entity);
  });
}

function buildInvestigationLayersSection(
  investigation: any,
  graphData: ViewerGraphData,
  nodeMap: Map<string, ViewerNode>,
  subject: DossierSubject,
): Record<string, unknown>[] {
  const rows = collectInvestigationLayerRows(graphData, nodeMap, subject);
  if (!rows.length) return [];

  const layerMap = new Map<number, InvestigationLayerRow[]>();
  rows.forEach((row) => {
    if (!layerMap.has(row.layer)) layerMap.set(row.layer, []);
    layerMap.get(row.layer)!.push(row);
  });

  const primaryQueryNode = graphData.nodes.find((node) => {
    const metadata = node.raw?.metadata || {};
    return metadata.layer === 1 && metadata.parentNodeId === 'starter';
  });

  const blocks: Record<string, unknown>[] = [
    {
      text: 'Investigation Layers',
      fontSize: 13,
      bold: true,
      color: '#0f172a',
      margin: [0, 14, 0, 8],
    },
    {
      text: [
        { text: 'Root query: ', color: '#64748b' },
        { text: `${subject.rootTypeLabel} ${subject.rootValue}`, bold: true, color: '#0f172a' },
      ],
      fontSize: 9,
      margin: [0, 0, 0, 4],
    },
  ];

  if (primaryQueryNode) {
    blocks.push({
      text: [
        { text: 'Layer 1 anchor: ', color: '#64748b' },
        { text: primaryQueryNode.label, color: '#334155' },
        { text: ' — connected from root via OSINT search', color: '#64748b' },
      ],
      fontSize: 8,
      margin: [0, 0, 0, 6],
    });
  }

  blocks.push({
    text:
      'Each layer shows how entities were discovered: who they connect from, what action created them, and the relationship link.',
    fontSize: 8,
    color: '#64748b',
    lineHeight: 1.35,
    margin: [0, 0, 0, 8],
  });

  Array.from(layerMap.keys())
    .sort((a, b) => a - b)
    .forEach((layerNumber) => {
      const layerRows = layerMap.get(layerNumber) || [];
      blocks.push({
        text: `Layer ${layerNumber} (${layerRows.length}) — ${getLayerDescription(layerNumber, investigation, subject)}`,
        fontSize: 9,
        bold: true,
        color: '#334155',
        margin: [0, 6, 0, 4],
      });
      blocks.push({
        table: {
          headerRows: 1,
          widths: [92, 52, 88, 68, '*'],
          body: [
            [
              headerCell('Entity'),
              headerCell('Type'),
              headerCell('Connected from'),
              headerCell('Action'),
              headerCell('Link'),
            ],
            ...layerRows.map((row) => [
              cell(row.entity, { bold: true, color: '#0f172a', fontSize: 7 }),
              cell(row.entityType, { fontSize: 7 }),
              cell(row.parent, { fontSize: 7, color: '#64748b' }),
              cell(row.action, { fontSize: 7 }),
              cell(row.relationship, { fontSize: 7, italics: true, color: '#1d4ed8' }),
            ]),
          ],
        },
        layout: {
          hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length ? 1 : 0.5),
          vLineWidth: () => 0,
          hLineColor: () => '#e2e8f0',
          paddingLeft: () => 4,
          paddingRight: () => 4,
          paddingTop: () => 4,
          paddingBottom: () => 4,
        },
        margin: [0, 0, 0, 8],
      });
    });

  return blocks;
}

function buildRelationshipMapSection(
  graphData: ViewerGraphData,
  nodeMap: Map<string, ViewerNode>,
): Record<string, unknown>[] {
  return [
    { text: 'Relationship Map', fontSize: 13, bold: true, color: '#0f172a', margin: [0, 14, 0, 8] },
    {
      table: {
        headerRows: 1,
        widths: ['*', 80, '*'],
        body: [
          [headerCell('Source'), headerCell('Relationship'), headerCell('Target')],
          ...graphData.edges.map((edge) => [
            cell(nodeMap.get(edge.source)?.label ?? edge.source, { color: '#0f172a' }),
            cell(edge.label, { italics: true, color: '#1d4ed8' }),
            cell(nodeMap.get(edge.target)?.label ?? edge.target, { color: '#0f172a' }),
          ]),
        ],
      },
      layout: {
        hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length ? 1 : 0.5),
        vLineWidth: () => 0,
        hLineColor: () => '#e2e8f0',
        paddingLeft: () => 4,
        paddingRight: () => 4,
        paddingTop: () => 4,
        paddingBottom: () => 4,
      },
    },
  ];
}

/**
 * Builds a pdfMake document matching link_analysis_viewer `/report` dossier layout,
 * with an additional graph screenshot section.
 */
export function buildLinkAnalysisDossierPdfDefinition(
  input: LinkAnalysisDossierPdfInput,
): Record<string, unknown> {
  const investigation = input.investigation || {};
  const graphData = input.graphData;
  const correlations = input.correlations || [];
  const degreeMap = getDegreeMap(graphData);
  const nodeMap = new Map(graphData.nodes.map((node) => [node.id, node]));
  const subject = resolveDossierSubject(investigation, graphData);
  const generatedAt = format(new Date(), 'EEEE, MMMM d, yyyy, h:mm a');
  const exportedBy = input.exportedBy?.trim() || 'RYNO OSINT Link Analysis';

  const secondaryFields = subject.discovered.filter(
    (field) => field.label !== subject.rootTypeLabel,
  );
  const leftHeaderFields = [
    {
      label: subject.rootTypeLabel,
      value: subject.rootValue,
    },
    ...secondaryFields.slice(0, 1),
  ];
  const rightHeaderFields = [
    ...secondaryFields.slice(1),
    { label: 'Classification', value: 'Confidential' },
  ];

  const riskCounts = { high: 0, medium: 0, low: 0 };
  graphData.nodes.forEach((node) => {
    const risk = deriveMeta(node, degreeMap[node.id] ?? 0).risk;
    riskCounts[risk]++;
  });

  const content: Record<string, unknown>[] = [
    { text: 'OSINT LINK ANALYSIS', fontSize: 9, bold: true, color: '#1d4ed8' },
    { text: 'Investigation Dossier', fontSize: 22, bold: true, color: '#0f172a', margin: [0, 4, 0, 10] },
    {
      text: [
        { text: 'Subject: ', color: '#64748b' },
        { text: subject.name, bold: true, color: '#0f172a' },
      ],
      fontSize: 10,
      margin: [0, 0, 0, 8],
    },
    {
      columns: [
        {
          width: '50%',
          stack: [
            ...leftHeaderFields.map((field, index) => ({
              text: [
                { text: `${field.label}: `, color: '#64748b' },
                { text: field.value, color: '#334155' },
              ],
              fontSize: 9,
              margin: index > 0 ? ([0, 4, 0, 0] as [number, number, number, number]) : undefined,
            })),
            {
              text: [{ text: 'Generated: ', color: '#64748b' }, { text: generatedAt, color: '#334155' }],
              fontSize: 9,
              margin: [0, 4, 0, 0],
            },
          ],
        },
        {
          width: '50%',
          stack: rightHeaderFields.map((field, index) => ({
            text: [
              { text: `${field.label}: `, color: '#64748b' },
              { text: field.value, color: '#334155' },
            ],
            fontSize: 9,
            margin: index > 0 ? ([0, 4, 0, 0] as [number, number, number, number]) : undefined,
          })),
        },
      ],
      margin: [0, 0, 0, 14],
    },
    {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 2, lineColor: '#0f172a' }],
      margin: [0, 0, 0, 14],
    },
    { text: 'Executive Summary', fontSize: 13, bold: true, color: '#0f172a', margin: [0, 0, 0, 8] },
    buildSummaryCards(graphData.nodes.length, graphData.edges.length, correlations.length, riskCounts.high),
    {
      text:
        `This report compiles ${graphData.nodes.length} entities and ${graphData.edges.length} relationships discovered ` +
        `across open-source intelligence around the subject. ${correlations.length} cross-platform correlation(s) were ` +
        'detected, including shared usernames, common locations, and face matches across profiles.',
      fontSize: 9,
      color: '#475569',
      lineHeight: 1.35,
      margin: [0, 0, 0, 12],
    },
  ];

  content.push(...buildInvestigationLayersSection(investigation, graphData, nodeMap, subject));

  if (input.graphScreenshotDataUrl) {
    content.push(
      { text: 'Graph Visualization', fontSize: 13, bold: true, color: '#0f172a', margin: [0, 0, 0, 8] },
      {
        image: input.graphScreenshotDataUrl,
        width: 500,
        alignment: 'center',
        margin: [0, 0, 0, 12],
      },
    );
  }

  content.push(...buildCorrelationsSection(correlations, nodeMap));
  content.push(...buildEntityInventorySection(graphData, degreeMap));
  content.push(...buildRelationshipMapSection(graphData, nodeMap));
  content.push(...buildEvidenceImagesSection(input.evidenceImages || []));

  content.push({
    text: 'Generated by OSINT Link Analysis · For authorized investigative use only · Data compiled from open sources.',
    fontSize: 7,
    color: '#94a3b8',
    margin: [0, 18, 0, 0],
  });

  const footerText = `${exportedBy} · Investigation ${investigation.link_analysis_id || ''}`;

  return {
    pageSize: 'A4',
    pageMargins: [40, 42, 40, 48],
    content,
    footer: (currentPage: number, pageCount: number) => ({
      columns: [
        { text: footerText, fontSize: 7, color: '#94a3b8', alignment: 'left' },
        { text: `Page ${currentPage} of ${pageCount}`, fontSize: 7, color: '#94a3b8', alignment: 'right' },
      ],
      margin: [40, 0, 40, 16],
    }),
    defaultStyle: {
      fontSize: 9,
    },
  };
}
